import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { ref, onValue } from "firebase/database";
import { db } from "./firebase";
import logoPath from "./assets/x_math_logo.png";
import { formatCourseId } from "./utils/formatCourse";

export const generateStudentReport = async (student) => {
  // Helper to fetch data from a ref and return a promise
  const fetchData = (refPath) => {
    return new Promise((resolve) => {
      const r = ref(db, refPath);
      onValue(r, (snapshot) => {
        resolve(snapshot.exists() ? snapshot.val() : {});
      }, { onlyOnce: true });
    });
  };

  // Fetch homeworks and exams for the student's course
  const courseId = student.course || student.enrolledCourse;
  const [homeworks, exams] = await Promise.all([
    fetchData(`courses/${courseId}/homeworks`),
    fetchData(`courses/${courseId}/exams`)
  ]);

  // Load logo as an image and add to PDF
  return new Promise((resolve) => {
    const doc = new jsPDF();
    const currentDate = new Date();
    const img = new window.Image();
    img.src = logoPath;
    img.onload = () => {
      const logoW = 42;
      const logoH = logoW * (img.naturalHeight / img.naturalWidth);
      doc.addImage(img, "PNG", 14, 6, logoW, logoH);
      doc.setFontSize(18);
      doc.setTextColor(40);
      doc.text(`Student Report: ${student.name}`, 60, 20);
      doc.setFontSize(12);
      doc.text(`Student ID: ${student.studentId}`, 14, 30);
      doc.text(`Email: ${student.email || "N/A"}`, 14, 36);
      doc.text(`Course: ${formatCourseId(student.course || student.enrolledCourse || "N/A")}`, 14, 42);

      // Table header style
      const headerStyle = {
        fillColor: [200, 200, 200],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        halign: 'center'
      };

      // Exam Grades Table
      const examEntries = exams && Object.keys(exams).length > 0 ? Object.entries(exams) : [];
      let examData = [];
      if (examEntries.length > 0) {
        doc.setFontSize(14);
        doc.text("Exam Grades", 14, 60);
        examData = examEntries.map(([examId, exam]) => {
          const studentExam = student.examGrades && student.examGrades[examId] ? student.examGrades[examId] : {};
          return [
            exam.name || examId,
            exam.date || "N/A",
            exam.totalMark || "N/A",
            studentExam.grade || "N/A",
            studentExam.mark || "N/A",
            studentExam.comment || "N/A"
          ];
        });
        autoTable(doc, {
          startY: 65,
          head: [['Exam', 'Date', 'Total Mark', 'Grade', 'Mark', 'Comment']],
          body: examData,
          styles: { cellPadding: 3, fontSize: 10, valign: 'middle' },
          headStyles: headerStyle,
          margin: { left: 14, right: 14 },
          tableWidth: 'auto',
          columnStyles: { 0: { cellWidth: 'auto' }, 1: { cellWidth: 'auto' }, 2: { cellWidth: 'auto' }, 3: { cellWidth: 'auto' }, 4: { cellWidth: 'auto' }, 5: { cellWidth: 'auto' } }
        });
      } else {
        doc.text("No exam grades available", 14, 60);
      }

      // Homework Status Table
      const currentY = doc.lastAutoTable?.finalY || 70;
      let homeworkData = [];

      // First, check if student has homework status data
      if (student.homeworkStatus && Object.keys(student.homeworkStatus).length > 0) {
        const studentHwStatus = student.homeworkStatus;
        const homeworkEntries = homeworks && Object.keys(homeworks).length > 0 ? Object.entries(homeworks) : [];

        // Create a map of course homeworks for easy lookup
        const courseHomeworkMap = {};
        homeworkEntries.forEach(([hwId, hw]) => {
          courseHomeworkMap[hwId] = hw;
        });

        // Process student's homework status
        homeworkData = Object.entries(studentHwStatus).map(([hwId, status]) => {
          const courseHw = courseHomeworkMap[hwId];
          return [
            courseHw ? (courseHw.title || hwId) : hwId,
            courseHw ? (courseHw.dueDate ? formatDate(courseHw.dueDate) : "No due date") : "N/A",
            formatStatus(status)
          ];
        });
      }

      doc.setFontSize(14);
      doc.text("Homework Status", 14, currentY + 10);

      if (homeworkData.length > 0) {
        autoTable(doc, {
          startY: currentY + 15,
          head: [['Homework Name', 'Due Date', 'Status']],
          body: homeworkData,
          styles: { cellPadding: 3, fontSize: 10, valign: 'middle' },
          headStyles: headerStyle,
          margin: { left: 14, right: 14 },
          tableWidth: 'auto',
          columnStyles: { 0: { cellWidth: 'auto' }, 1: { cellWidth: 'auto' }, 2: { cellWidth: 'auto' } },
          didParseCell: (data) => {
            if (data.column.index === 2 && data.row.index >= 0) {
              const status = data.cell.raw.toLowerCase();
              if (status === 'done') {
                data.cell.styles.fillColor = [220, 255, 220];
              } else if (status === 'incomplete') {
                data.cell.styles.fillColor = [255, 255, 200];
              } else if (status === 'missing') {
                data.cell.styles.fillColor = [255, 220, 220];
              }
            }
          }
        });
      } else {
        doc.text("No homework data available", 14, currentY + 15);
      }

      // Finalize and save PDF
      const date = formatDate(currentDate);
      doc.setFontSize(10);
      doc.setTextColor(150);
      doc.text(`Report generated on ${date}`, 14, doc.lastAutoTable.finalY + 15);
      doc.save(`Student_Report_${student.name.replace(/\s+/g, '_')}_${date.replace(/\//g, '-')}.pdf`);
      resolve();
    };
  });
};

// Helper function to format date
const formatDate = (dateString) => {
  const date = new Date(dateString);
  return isNaN(date.getTime())
    ? dateString
    : `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
};

// Helper function to format status
const formatStatus = (status) => {
  switch (status.toLowerCase()) {
    case 'done':
      return 'Done';
    case 'incomplete':
      return 'Incomplete';
    case 'missing':
      return 'Missing';
    default:
      return status;
  }
};